<template>
  <div class="relative flex flex-col items-center bg-white shadow-lg rounded-xl p-4 overflow-hidden flex-1 w-full">
    <div class="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-b from-[#424c42] via-[#bbc1a9] to-white"></div>
      
      <div class="relative flex flex-col items-center w-full space-y-3 flex-1">
        <!-- País -->
        <span class="text-xs font-semibold text-white px-2 py-0 rounded-full uppercase">
          {{ itemMeeting.acreditado.tipoAcreditado.descripcion }}
        </span>

        <!-- Logo -->
        <img v-if="itemMeeting.acreditado.foto" :src="itemMeeting.acreditado.foto"
          class="w-28 h-28 object-scale-down rounded-full border-2 border-white shadow-md bg-white" />
        <div v-else
          class="w-28 h-28 object-fi rounded-full border-2 border-white shadow-md flex items-center justify-center font-semibold text-3xl text-gray-700 bg-[radial-gradient(circle,_white_0%,_#d1d5db_100%)]">
          <span class="leading-none text-gray-500 mb-1 text-xl">
            {{ iniciales(itemMeeting.acreditado.nombre, itemMeeting.acreditado.apellido) }}
          </span>
        </div>

        <!-- Datos del acreditado -->
        <div class="space-y-0 pt-1">
          <h3 class="text-base font-bold text-gray-800 text-center">{{ itemMeeting.acreditado.nombre + " " + itemMeeting.acreditado.apellido }}</h3>
          <p class="text-sm text-gray-500 text-center">{{ itemMeeting.acreditado.cargo || 'N/A' }}</p>
        </div>

        <div class="w-full space-y-0 pt-1 pb-4" v-if="usarResumenAcreditado">
          <p class="text-sm text-gray-500 text-center line-clamp-3" v-tooltip.top="{value: itemMeeting.resumenAcreditado, escape: false,   pt: { text: { style: { minWidth: '400px' } }}}" v-html="itemMeeting.resumenAcreditado || '&nbsp;'"></p>
        </div>

        <div v-if="usarPerfilEmpresas">
          <!-- Avatars de acreditados -->
          <div class="flex flex-col items-center space-y-1 justify-center pt-2" >
            <img  v-if="itemMeeting.empresa && itemMeeting.empresa.previewLogo"  :src="itemMeeting.empresa.previewLogo" class="w-10 h-10 rounded-full border border-[#bbc1a9] object-cover bg-white"/>
            <div v-else class="w-10 h-10 rounded-full border border-[#bbc1a9] bg-gray-300"></div>
            <p class="text-sm text-gray-500 font-semibold leading-none">{{ itemMeeting.empresa?.nombreFantasia || 'Sin empresa' }}</p>
          </div>

          <div class="flex items-center" >
            <button @click="mostrarDetalleEmpresaAcreditado" class="text-[#424c42] text-xs inline-block origin-center transition hover:text-gray-600 hover:scale-110 cursor-pointer">
              <font-awesome-icon :icon="['fas', 'fa-circle-plus']"  /> INFO
            </button>
          </div>
        </div>

        <button :disabled="!permiteSolicitarReuniones" 
          v-tooltip.top="!permiteSolicitarReuniones ? 'No tiene permisos para solicitar reuniones. Solo puede aceptar solicitudes' : null"
          @click="solicitarReunion" 
          class="w-full !mt-auto cursor-pointer text-sm font-bold text-[#424c42] bg-transparent py-3 border border-[#424c42] shadow-md rounded-lg transform transition-transform duration-200 hover:scale-[102%] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:border-gray-300 disabled:text-gray-400">
          {{ $t('boton.solicitar.reunion') }}
        </button>

      </div>
  </div> 

  <DetalleEmpresaView v-if="detalleEmpresaVisible" :empresa="empresa" :closeFunction="cerrarDetalleEmpresa" />
  <FullScreenLoader :visible="isSubmitting" />

</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Swal from 'sweetalert2'

import FullScreenLoader from "@/components/commons/FullScreenLoader.vue";

import { ConfiguracionMeeting, Empresa, ItemMeeting } from 'qreventos-suite/src/services/suite.interfaces';
import DetalleEmpresaView from '@/components/views/private/modal/DetalleEmpresaView.vue';
import empresaService from 'qreventos-suite/empresas';
import configuracionMeetingService from '@/services/configuracion.merting.services';

export default defineComponent({
  components: {
    DetalleEmpresaView, FullScreenLoader
  },
  props: {
    onSolicitarReunion: {
      type: Function,
      required: false
    },
    itemMeeting: {
      type: {} as PropType<ItemMeeting>,
      required: true
    },
    permiteSolicitarReuniones: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      isSubmitting: false,
      detalleEmpresaVisible: false,
      empresa: undefined as unknown as Empresa,
      usarPerfilEmpresas: false,
      usarResumenAcreditado: false,
    };
  },
  methods: {
    iniciales(nombre: string, apellido: string): string {
      const n = nombre?.charAt(0) || '';
      const a = apellido?.charAt(0) || '';
      return (n + a).toUpperCase();
    },
    async mostrarDetalleEmpresaAcreditado() {
      if (!this.itemMeeting.empresa) {
        Swal.fire({ title: this.$t('global.atencion'), html: this.$t("label.acreditado.no.cargo.empresa"), icon: "warning"});
        return;
      }

      try {
        this.isSubmitting = true;
        const configuracionMeeting: ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;
        
        this.empresa = await empresaService.findEmpresaBy(configuracionMeeting.evento.id, this.itemMeeting.empresa.id);
        this.detalleEmpresaVisible = true;

      } catch (error) {
        this.isSubmitting = false;
        Swal.fire({ title: "Error", text: 'Error [' + error + ']', icon: "error" });
      } finally {
        this.isSubmitting = false;
      }

    },
    solicitarReunion() {
      if(this.onSolicitarReunion) {
        this.onSolicitarReunion(this.itemMeeting);
      }
    },
    cerrarDetalleEmpresa() {
      this.detalleEmpresaVisible = false;
    },
  },
  mounted() {
    const configuracionMeeting: ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;
    this.usarResumenAcreditado = (configuracionMeeting.campoResumenAcreditado != null);
    this.usarPerfilEmpresas = configuracionMeeting.usarPerfilEmpresas;
  }
});
</script>
